import { CONFIG } from "~/config";
import MyMapsDialog from "./mymaps-dialog";
import { _ } from "~/i18n";
import SceneCoords from "~/scene/common/coords";
import { node } from "~/util/dom";

interface IState {
	tponly: boolean;
	shrink: boolean;
	single: boolean;
}

type TAllDataTypes = {
	name: "route",
	data: {
		lang: string;
		rp_c: Array<number>;
		xc?: Array<string>;
		tp_c?: Array<number>;
		tp_w?: Array<string>;
		tp_i?: Array<number>;
		rg: string;
		rs: Array<string>;
		ri: Array<number>;
		rut?: Array<number>;
		rp_aw?: Array<string>;
		name?: string;
		title: string;
	};
	tponly: boolean;
} | {
	name: "activity",
	data: {
		mid: string;
		title: string;
	};
} | {
	name: "distancemeter",
	data: {
		criterion: "hand";
		geometry: string;
		title: string;
	};
} | {
	name: "path",
	data: {
		mid: string;
		title: string;
	};
} | {
	name: "folder",
	data: {
		mid: string;
		title: string;
	};
	isEmpty: boolean;
	hasTracks: boolean;
} | {
	name: "usermarks",
	data: {
		ut: string;
		uc: string;
		ud: string;
		title: string;
		usertitle: string;
		subtitle: string;
		description?: string;
	};
};

function getNameFromData(data: TAllDataTypes) {
	switch (data.name) {
		case "activity":
			return data.data.title;

		case "distancemeter":
			return data.data.title;

		case "folder":
			return data.data.title;

		case "path":
			return data.data.title;

		case "route":
			return data.data.title || "";

		case "usermarks":
			return data.data.usertitle || data.data.title;

		default:
	}

	return "";
}

function appendObjToUsp(obj: any, usp: URLSearchParams) {
	const keys = Object.keys(obj);

	for (const key of keys) {
		const value = obj[key];

		if (Array.isArray(value)) {
			for (const item of value) {
				usp.append(key, decodeURIComponent(item.toString()));
			}
		} else {
			usp.append(key, decodeURIComponent(value.toString()));
		}
	}
}

function createLine(title: string, checked: boolean, onChange: (checked: boolean) => void) {
	const line = node("div", { className: "mymaps__exportDialog__optsLine" });
	const toggle = node("mapy-toggle", { checked });
	const textPart = node("div", { className: "mymaps__exportDialog__optsTxt", textContent: title });

	textPart.addEventListener("click", () => {
		toggle.checked = !toggle.checked;
		onChange(toggle.checked);
	});
	toggle.addEventListener("change", () => {
		onChange(toggle.checked);
	});
	line.append(toggle, textPart);

	return line;
}

interface IErrorData {
	routeError410: boolean;
}

interface IMyMapsExport {
	data: TAllDataTypes;
	filename?: string;
	fileTypeFromHeader?: boolean;
	onStartDownload?: () => void;
	onExport?: () => void;
	onError?: (errorData: IErrorData) => void;
}

const FORMAT = "gpx";

/**
 * routovac - boolean Exportovat trasy autem pro použití v autonavigaci
 * aktivita - prazdna
 * cesta - prazdna
 * vlastni body/mereni - prazdna
 * slozka - Exportovat trasy autem pro použití v autonavigaci (Volba “Exportovat trasy autem pro použití v navigaci” se zobrazí jen pokud se exportuje alespoň 1 trasa)
 */
export default class MyMapsExport {
	_opts: IMyMapsExport;

	_dialog: MyMapsDialog;

	_state: IState;

	_coords: Array<SceneCoords>;

	_addLog: {[key: string]: any;};

	constructor(data: IMyMapsExport) {
		this._opts = {
			data,
			filename: "export",
			fileTypeFromHeader: false,
			onStartDownload: () => {},
			onExport: () => {},
			onError: () => {},
			...data,
		};
		this._state = {
			tponly: false,
			shrink: false,
			single: true,
		};
		this._coords = [];
		this._addLog = {};

		this._build();
	}

	open() {
		this._dialog?.open();
	}

	destroy() {
		this._dialog?.destroy();
	}

	_closeDialog(force?: boolean) {
		this._dialog.close(force);
	}

	_build() {
		const dataName = getNameFromData(this._opts.data);
		const mainTitle = dataName
			? _("mymap.export.title", { name: dataName })
			: _("action.download.title");
		const dialog = new MyMapsDialog({
			titles: {
				main: [mainTitle],
				saveBtn: _("mymap.contextMenu.export"),
				cancelBtn: _("cancel"),
			},
			onThrowAwayConfirm: () => {
				this._closeDialog(true);
			},
			onSave: () => {
				this._onSave();
			},
			onCancel: () => {
				this._closeDialog(true);
			},
		});
		const contents = dialog.contents;
		const mainContent = contents[0];
		const opts = node("div", { className: "mymaps__exportDialog__opts" });
		let hasOpts = false;

		mainContent.classList.add("mymaps-dialog__defContent");
		mainContent.classList.add("mymaps__export__mainContent");

		// slozka
		if (this._opts.data.name === "folder") {
			if (this._opts.data.isEmpty) {
				const info = node("p", { className: "mymaps__exportDialog__emptyInfo", textContent: _("mymap.export.emptyFolder") });

				mainContent.append(info);
				dialog.dom.saveBtn.disabled = true;
			} else {
				opts.append(createLine(_("mymap.places.exportMultiroute.switch.shrink2"), false, checked => {
					this._state.shrink = checked;
				}));
				opts.append(createLine(_("mymap.places.exportMultiroute.switch.single2"), true, checked => {
					this._state.single = checked;
				}));
				hasOpts = true;
			}
		}

		// trasy autem pro autonavigaci
		if ((this._opts.data.name === "folder" && this._opts.data.hasTracks) || (this._opts.data.name === "route" && this._opts.data.tponly)) {
			opts.append(createLine(_("mymap.places.exportMultiroute.switch.tponly"), false, checked => {
				this._state.tponly = checked;
			}));
			hasOpts = true;
		}

		hasOpts && mainContent.append(opts);
		this._dialog = dialog;
	}

	/**
	 * Jde o speciální typ?
	 */
	get _isExcluded() {
		return this._opts.data.name === "folder" || this._opts.data.name === "route";
	}

	_log() {
		const opts = {
			format: FORMAT,
			// route, gpx...
			type: this._opts.data.name,
			...this._addLog,
		};

		Mapy.getComponent("logger").log("export_gpx_kml", opts);
	}

	_onSave() {
		this._dialog.error = "";
		this._dialog.loading = true;
		this._opts.onStartDownload();


		const data: any = {
			export: [FORMAT],
		};
		let url = "";
		const usp = new URLSearchParams();

		switch (this._opts.data.name) {
			case "route": {
				url = `${CONFIG.requestUrl}/tplannerexport`;
				usp.append("export", FORMAT);
				appendObjToUsp(this._opts.data.data, usp);

				// tponly zaskrtnute
				if (this._opts.data.tponly) {
					usp.append("tponly", this._state.tponly ? "0" : "1");
				}
				break;
			}

			case "folder":
				url = `${CONFIG.requestUrl}/mapybox-export/v1/folder/${FORMAT}`;
				usp.append("id", this._opts.data.data.mid);
				usp.append("export", FORMAT);
				usp.append("shrink", this._state.shrink.toString());
				usp.append("single", this._state.single.toString());
				// reverse
				usp.append("tponly", this._state.tponly ? "false" : "true");
				break;

			case "path":
				url = `${CONFIG.requestUrl}/mapybox-export/v1/path/${FORMAT}`;
				usp.append("id", this._opts.data.data.mid);
				break;

			case "activity":
				url = `${CONFIG.requestUrl}/mapybox-export/v1/track/${FORMAT}`;
				usp.append("tid", this._opts.data.data.mid);
				data.mid = [this._opts.data.data.mid];
				break;

			case "distancemeter":
			case "usermarks": {
				url = `${window.location.origin}/export`;

				const keys = Object.keys(this._opts.data.data);

				for (const key of keys) {
					const value = this._opts.data.data[key];

					data[key] = Array.isArray(value)
						? value
						: [value];
				}
				break;
			}

			default:
		}

		usp.append("rand", Math.random().toString());

		Mapy.Util.startDownload({
			url: `${url}?${usp.toString()}`,
			data: this._isExcluded ? {} : data,
			onSuccess: () => {
				this._log();
				this._opts.onExport();
				this._closeDialog(true);
			},
			onError: (error: any) => {
				const sendError: IErrorData = {
					routeError410: error?.status === 410,
				};

				this._log();

				if (sendError.routeError410) {
					this._closeDialog(true);
					this._opts.onError(sendError);
				} else {
					this._dialog.error = _("mymap.notifications.export.error");
					this._dialog.loading = false;
				}
			},
			filename: `${this._opts.filename}.${FORMAT}`,
			method: this._isExcluded ? "GET" : "POST",
			fileTypeFromHeader: this._opts.fileTypeFromHeader,
		});
	}
}
